import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { ProgramTypeApi } from "../../api/ProgramTypeApi";
import type { DeleteProgramTypeStaff } from "../../generated/dto/DeleteProgramTypeStaff";
import type { ProgramTypeSearchParams } from "../../generated/dto/ProgramTypeSearchParams";
import type { ProgramTypeStaff } from "../../generated/dto/ProgramTypeStaff";
import type { ProgramTypeStaffSearchResult } from "../../generated/dto/ProgramTypeStaffSearchResult";
import type { UpdateProgramType } from "../../generated/dto/UpdateProgramType";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class ProgramTypeApiClient implements ProgramTypeApi {
  constructor(private readonly client = useClient()) {}

  async getProgramTypeStaff(id: number): Promise<ProgramTypeStaff> {
    const { data } = await this.client.get<unknown>(`/programtype/staff/${id}`);
    return deserializeCbiDto<ProgramTypeStaff>(data, "generated.dto.ProgramTypeStaff");
  }

  async getAllProgramTypeStaffs(): Promise<Array<ProgramTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/programtype/staff/all`);
    return deserializeCbiDto<Array<ProgramTypeStaff>>(data, "generated.dto.ProgramTypeStaff");
  }

  async updateProgramTypeStaff(source: UpdateProgramType): Promise<ProgramTypeStaff> {
    const { data } = await this.client.put<unknown>(`/programtype/staff/${source.programId}`, serializeCbiDto(source, "generated.dto.UpdateProgramType"));
    return deserializeCbiDto<ProgramTypeStaff>(data, "generated.dto.ProgramTypeStaff");
  }

  async deleteProgramTypeStaff(source: DeleteProgramTypeStaff): Promise<void> {
    await this.client.delete(`/programtype/staff/${source.programId}`);
  }

  async searchProgramTypeStaff(params: ProgramTypeSearchParams, page: number, perPage: number): Promise<PaginationResult<ProgramTypeStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/programtype/staff/search`, serializeCbiDto(params, "generated.dto.ProgramTypeSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<ProgramTypeStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<ProgramTypeStaffSearchResult>>(result.items, "generated.dto.ProgramTypeStaffSearchResult") };
    return result;
  }

  async searchProgramTypeStaffFull(params: ProgramTypeSearchParams, page: number, perPage: number): Promise<PaginationResult<ProgramTypeStaff>> {
    const { data } = await this.client.post<unknown>(`/programtype/staff/search/full`, serializeCbiDto(params, "generated.dto.ProgramTypeSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<ProgramTypeStaff>;
    result = { ...result, items: deserializeCbiDto<Array<ProgramTypeStaff>>(result.items, "generated.dto.ProgramTypeStaff") };
    return result;
  }
}

export const useProgramTypeApi = (): ProgramTypeApi => new ProgramTypeApiClient();
